import React, { useState, useEffect, useMemo } from "react";
import {
  Box,
  Chip,
  Grid,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import SearchOffRoundedIcon from "@mui/icons-material/SearchOffRounded";
import LocalizedInput from "../LocalizedInput";
import { emptyLocalizedFields } from "../../../config/localization";
import TextInput from "../TextInput";
import isEqual from "lodash.isequal";
import VirtualizedAutoCompleteField from "../VirtualizedAutoCompleteField";
import { useData } from "../../../contexts/DataContext/DataContextt";
import { usePreparationData } from "../../../contexts/hooks/usePreparationData";
import { useSearch } from "../../../contexts/hooks/useReusableHook";
import { fetchClasses } from "../../../services/classService";
import { buildQueryString } from "../../../helpers/buildQueryString";
import { usePreload } from "../../../contexts/DataContext/PreloadContext";
import TabulatedGridCollapsibleV2 from "../TabulatedGridCollapsibleV2";
import { useNavigate } from "react-router-dom";
import { StickyFormActions } from "../StickyFormActions";
import { CardHeading, cardSx } from "../CardHeading";
import { useUnsavedChangesGuard } from "../../../contexts/hooks/useUnsavedChangesGuard";

export const GroupForm = ({ props, onSave }) => {
  const { language } = useData();
  const { withLoader } = usePreload();
  const navigate = useNavigate();

  const { classOptions } = usePreparationData({ sources: ["classes"] });
  const {
    options: classLocalOptions,
    search: searchClasses,
    searching: isClassSearching,
  } = useSearch(classOptions, fetchClasses, {
    displayNameFields: ["classDisplayName", "className"],
    extraKeys: [],
  });

  const defaultGroupValue = useMemo(
    () => ({
      groupCode: props.row.groupCode ?? "",
      groupClasses: props.row.groupClasses ?? "",
      /*groupName: {
        ...emptyLocalizedFields,
        [language]: props.row.groupName || "",
      },
      groupDisplayName: {
        ...emptyLocalizedFields,
        [language]: props.row.groupDisplayName || "",
      },*/
      groupName: props.row.groupName || emptyLocalizedFields,
      groupDisplayName: props.row.groupDisplayName || emptyLocalizedFields,
    }),
    [props.row, language],
  );

  const [groupValueOriginal, setGroupValueOriginal] =
    useState(defaultGroupValue);
  const [groupValue, setGroupValue] = useState(defaultGroupValue);
  // in edit mode the group's classes (and final form values) load after mount
  const [classesLoading, setClassesLoading] = useState(!!props.isUpdate);

  const saveText = props?.saveText ?? "Save";

  const handleSave = async () => {
    // keep the edits (and the unsaved-changes guard) when the save fails
    const saved = await onSave(groupValue);
    if (saved === false) return;

    if (!props.isUpdate) {
      setGroupValue({ ...defaultGroupValue });
      setGroupValueOriginal({ ...defaultGroupValue });
    } else setGroupValueOriginal(groupValue);
  };

  useEffect(() => {
    if (!defaultGroupValue.groupCode) return;

    const query = buildQueryString({
      searchOn: "classGroupCode",
      language: language,
      page: 0,
      rowsPerPage: 9000,
      searchTerm: `${defaultGroupValue.groupCode}`,
    });

    setClassesLoading(true);
    withLoader(async () => {
      try {
        const response = await fetchClasses(query);
        const includedClasses = response.results.map((m) => ({
          code: `${m.classCode}`,
          displayName: `${m.classDisplayName || m.className}`,
        }));

        let updatedGroup = {
          ...defaultGroupValue,
          includedClasses: includedClasses,
        };

        setGroupValue(updatedGroup);
        setGroupValueOriginal(updatedGroup);
      } finally {
        setClassesLoading(false);
      }
    });
  }, [defaultGroupValue]);

  const hasChanges = !isEqual(groupValue, groupValueOriginal);
  const { confirmLeave } = useUnsavedChangesGuard(hasChanges);
  const headers = [
    {
      prop: "code",
      text: "Code",
      width: 100,
      minWidth: 100,
    },
    {
      prop: "displayName",
      text: "Name",
      width: 100,
      minWidth: 100,
    },
  ];

  const handleCancel = async () => {
    if (!(await confirmLeave())) return;
    navigate("/groups");
  };

  return (
    <Box
      sx={{
        // keep focused controls clear of the sticky action bar (WCAG 2.4.11)
        "& input, & textarea, & button": {
          scrollMarginBottom: { xs: 168, sm: 96 },
        },
      }}
    >
      <Grid container spacing={3}>
        <Grid size={{ xs: 12, lg: 7 }}>
          <Paper
            component="section"
            variant="outlined"
            aria-labelledby="group-details-heading"
            aria-busy={classesLoading}
            sx={cardSx}
          >
            <CardHeading
              id="group-details-heading"
              title="Group details"
              helper="Identify the group and provide its localized names."
            />
            <Stack spacing={3}>
              <TextInput
                label="Group Code"
                required
                disabled={props.isUpdate}
                helperText={
                  props.isUpdate
                    ? "The group code can't be changed after creation"
                    : undefined
                }
                value={groupValue.groupCode}
                onChange={(e) => {
                  setGroupValue((prev) => ({
                    ...prev,
                    groupCode: e.target.value,
                  }));
                }}
              />
              <LocalizedInput
                variant="modern"
                label="Group Name"
                value={groupValue.groupName}
                onChange={(val) => {
                  setGroupValue((prev) => ({ ...prev, groupName: val }));
                }}
                disabled={classesLoading}
              />
              <LocalizedInput
                variant="modern"
                label="Group DisplayName"
                value={groupValue.groupDisplayName}
                onChange={(val) => {
                  setGroupValue((prev) => ({
                    ...prev,
                    groupDisplayName: val,
                  }));
                }}
                rows={2}
                disabled={classesLoading}
              />
            </Stack>
          </Paper>
        </Grid>
        <Grid size={{ xs: 12, lg: 5 }}>
          <Paper
            component="section"
            variant="outlined"
            aria-labelledby="group-classes-heading"
            sx={cardSx}
          >
            <CardHeading
              id="group-classes-heading"
              title="Included classes"
              helper={
                props.isUpdate
                  ? "Classes currently linked to this group."
                  : "Classes are linked to a group after it's created."
              }
            >
              {props.isUpdate && !classesLoading && (
                <Chip
                  size="small"
                  label={groupValue.includedClasses?.length ?? 0}
                />
              )}
            </CardHeading>
            <TabulatedGridCollapsibleV2
              variant="modern"
              headers={headers}
              data={groupValue.includedClasses ?? []}
              height={400}
              loading={classesLoading}
              emptyState={
                <Stack
                  alignItems="center"
                  spacing={1}
                  sx={{ py: 8, color: "text.secondary" }}
                >
                  <SearchOffRoundedIcon sx={{ fontSize: 40 }} />
                  <Typography>
                    {props.isUpdate
                      ? "No classes are linked to this group"
                      : "No classes yet"}
                  </Typography>
                </Stack>
              }
            />
          </Paper>
        </Grid>
      </Grid>
      {/*
      <Box mt={3}>
        <VirtualizedAutoCompleteField
          options={classLocalOptions}
          limit={3}
          label="Select Classes"
          value={groupValue.groupClasses}
          onChange={(val) => {
            setGroupValue((prev) => ({ ...prev, groupClasses: val }));
          }}
          searching={isClassSearching}
          onSearch={searchClasses}
        />
      </Box>*/}
      <StickyFormActions
        hasChanges={hasChanges}
        saving={props.uploading}
        saveText={saveText}
        onCancel={handleCancel}
        onSave={handleSave}
      />
    </Box>
  );
};
